import { ReactNode, ComponentPropsWithoutRef } from 'react';

import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export const isValidContent = (content: ReactNode): boolean => {
  if (!content) {
    return false;
  }
  // For string content, check if it's a valid non-empty string
  if (typeof content === 'string') {
    const trimmed = content.trim();
    return trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined';
  }
  // For other ReactNode types (JSX elements, numbers, etc.), consider them valid
  return true;
};

interface InfoTooltipCustomProps {
  children: ReactNode;
  content: ReactNode;
  size?: 'default' | 'large' | 'auto';
  asChild?: boolean;
  delayDuration?: number;
  open?: boolean;
  /** Called when the tooltip wants to open or close. Pair it with `open` to control the tooltip. */
  onOpenChange?: (open: boolean) => void;
  /**
   * Tooltip variant:
   * - 'default': Standard tooltip matching the base design system style
   * - 'info': Simple info tooltip with dark background (default)
   * - 'rich': Unpadded tooltip for custom content like lists or multi-section cards
   */
  variant?: 'default' | 'info' | 'rich';
  /** Optional external URL — renders a "Learn more →" footer link below content. */
  learnMoreUrl?: string;
}

type InfoTooltipProps = InfoTooltipCustomProps &
  Omit<ComponentPropsWithoutRef<typeof TooltipContent>, 'children' | 'variant' | 'content'>;

/**
 * InfoTooltip component that wraps shadcn Tooltip with sensible defaults for informational tooltips.
 * Extends all TooltipContent props, allowing full customization while providing a custom size option.
 *
 * @param children - The trigger element that will show the tooltip on hover
 * @param content - The content to display in the tooltip (can be text or JSX elements)
 * @param size - Tooltip maximum width ('default': 160px, 'large': 240px, 'auto': no max-width)
 * @param asChild - Whether to merge props with the child element instead of wrapping it (default: true)
 * @param delayDuration - Delay in ms before tooltip appears on hover (default: 400)
 * @param open - Control tooltip open state: undefined (hover behavior), true (force open), false (force closed)
 * @param side - Tooltip position: 'top' | 'right' | 'bottom' | 'left' (default: 'bottom')
 * @param showArrow - Whether to display the arrow indicator (default: true)
 * @param variant - Tooltip style: 'info' for simple tooltips, 'rich' for complex content (default: 'info')
 * @param className - Additional CSS classes to apply to the tooltip content
 * @param ...props - All other TooltipContent props (align, sideOffset, etc.)
 */
export const InfoTooltip = ({
  children,
  content,
  size = 'default',
  asChild = true,
  delayDuration,
  open,
  onOpenChange,
  side = 'bottom',
  showArrow = true,
  variant = 'info',
  className,
  learnMoreUrl,
  ...props
}: InfoTooltipProps) => {
  const { t } = useTranslation();
  const maxWidthClass = size === 'default' ? 'max-w-40' : size === 'large' ? 'max-w-60' : '';
  const shouldOpen = isValidContent(content) ? open : false;
  const variantMap = { rich: 'timestamp', default: 'default', info: 'info' } as const;
  const tooltipVariant = variantMap[variant];

  const body = learnMoreUrl ? (
    <span className="flex flex-col gap-2">
      <span>{content}</span>
      <a
        href={learnMoreUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
      >
        {t('common.learn_more')}
        <ExternalLinkIcon size={12} />
      </a>
    </span>
  ) : (
    content
  );

  return (
    <Tooltip open={shouldOpen} onOpenChange={onOpenChange} delayDuration={delayDuration}>
      <TooltipTrigger asChild={asChild}>{children}</TooltipTrigger>
      <TooltipContent
        variant={tooltipVariant}
        showArrow={showArrow}
        side={side}
        className={cn(maxWidthClass, 'z-1300 text-left break-words', className)}
        {...props}
      >
        {body}
      </TooltipContent>
    </Tooltip>
  );
};
